import React, { useMemo, useRef, useState } from 'react';
import pluralize from 'pluralize';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';

import {
    type ILead,
    EMessageChannel,
    useSendMessages,
    useUpdateConversationsReadStatus, useUserSettings
} from 'src/services/api';
import { useUpdateLeads } from 'src/services/api';
import { ReadStatus } from 'src/api/landlord-messages-api';
import Dialog from 'src/components/dialog/Dialog';
import { getBestChannelForConversation, isConversation, type IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context';
import { Flex, FlexColumn } from 'src/components/flex';
import SelectSnippet from 'src/components/input/select-snippet/SelectSnippet';
import { formatPlaybookMessageForFrontend, formatPlaybookMessageForBackend } from 'src/common/playbook-variables/playbook-variable-utils';
import { getMissingVariable, snippetContentsWithVariablesFilled } from 'src/pages/landlord/leads/messages/components/snippets-modal/utils';
import type { Option } from 'src/components/input/select/Select';
import SnippetTemplateMessage from 'src/pages/landlord/snippets/components/form/snippet-template-message';
import { PlaybookVariable } from 'src/services/api';
import type { IConversation } from 'src/api/landlord-messages-api';
import { useLeadsByUuids } from 'src/services/api';

type Props = {
    open: boolean,
    onClose: () => void,
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
    loggedInUser: any;
    leads: ILead[];
    properties: any[];
};

export default function BulkMessageLeadsModal({
    open,
    onClose,
    selectedConversations,
    setSelectedConversations,
    loggedInUser,
    leads,
    properties,
}: Props) {
    const { mutateAsync: sendMessages } = useSendMessages();

    const {
        mutateAsync: updateReadStatus,
        isLoading: isUpdatingReadStatus,
        variables: updateReadStatusVariables,
    } = useUpdateConversationsReadStatus();

    const { mutateAsync: updateLeads, isLoading: isUpdatingLeads } = useUpdateLeads();

    const channelBuckets: Record<EMessageChannel, number> = {
        [EMessageChannel.EMAIL]: 0,
        [EMessageChannel.SMS]: 0,
        [EMessageChannel.FACEBOOK_MESSENGER]: 0,
    };
    const _leadUuids = selectedConversations.map((conversation) => conversation.lead_uuid);
    const leadsByUuidLocal = useLeadsByUuids(_leadUuids);

    selectedConversations.forEach((conversation) => {
        const lead = leadsByUuidLocal[conversation.lead_uuid];
        if (!lead) {
            console.error('Lead not found for conversation', conversation);
            return;
        }

        const chan = getBestChannelForConversation(conversation, lead);
        if (chan) {
            channelBuckets[chan] += 1;
        }
    });

    const numEmails = channelBuckets[EMessageChannel.EMAIL];
    const numSms = channelBuckets[EMessageChannel.SMS];
    const numFacebookMessages = channelBuckets[EMessageChannel.FACEBOOK_MESSENGER];

    const formRef = useRef<HTMLFormElement>(null);

    const [message, setMessage] = useState('');
    const [isSending, setSending] = useState(false);

    const unreadConversations = useMemo(() => {
        return selectedConversations.filter((conversation) => {
            return isConversation(conversation) && (conversation as IConversation).num_unread_messages > 0;
        });
    }, [selectedConversations]);

    const unownedConversations = useMemo(() => {
        return selectedConversations.filter((conversation) => {
            const lead = leadsByUuidLocal[conversation.lead_uuid];
            if (!lead) { return false; }

            const isOwnedByCurrentUser = lead.owner === loggedInUser.id;
            return !isOwnedByCurrentUser;
        });
    }, [selectedConversations, leads]);

    const { data: userSettings } = useUserSettings();

    const leadsWithMissingVariables = useMemo(() => {
        return selectedConversations.filter((conversation) => {
            const lead = leadsByUuidLocal[conversation.lead_uuid];
            if (!lead) { return false; }
            const leadFirstName = lead.name?.split(' ')[0];
            const property = lead.property_id ? properties.find((o) => o.id === lead.property_id) : null;
            const propertyAddress = property?.street_address;
            const propertyTourLink = property?.showing_scheduling_link?.link;
            const propertyApplicationLink = property?.application_link?.link;
            const rent = property?.rent ? property.rent.toString() : undefined;
            const numberOfBedrooms = property?.num_bedrooms ? property.num_bedrooms.toString() : undefined;
            const numberOfBathrooms = property?.num_bathrooms ? property.num_bathrooms.toString() : undefined;
            const emailSignature = userSettings?.signatures?.email?.text;
            const smsSignature = userSettings?.signatures?.sms?.text;

            const variableMap: Record<PlaybookVariable, string> = {
                [PlaybookVariable.LeadFirstName]: leadFirstName ?? '',
                [PlaybookVariable.PropertyAddress]: propertyAddress ?? '',
                [PlaybookVariable.PropertyTourLink]: propertyTourLink ?? '',
                [PlaybookVariable.PropertyApplicationLink]: propertyApplicationLink ?? '',
                [PlaybookVariable.Rent]: rent ?? '',
                [PlaybookVariable.NumberOfBedrooms]: numberOfBedrooms ?? '',
                [PlaybookVariable.NumberOfBathrooms]: numberOfBathrooms ?? '',
                [PlaybookVariable.SMSSignature]: smsSignature ?? '',
                [PlaybookVariable.EmailSignature]: emailSignature ?? '',
                [PlaybookVariable.YourName]: loggedInUser?.name ?? '',
                [PlaybookVariable.YourFirstName]: loggedInUser?.name?.split(' ')[0] ?? '',
            };

            return !!getMissingVariable(
                formatPlaybookMessageForBackend(message),
                variableMap,
            );
        });
    }, [selectedConversations, message, userSettings, loggedInUser]);

    const getOptionDisabled = useMemo(() => {
        return (option: Option<string | undefined>) => {
            if (!option.value) { return false; }
            // Check if all selected conversations are missing a variable from this snippet
            return selectedConversations.every((conversation) => {
                const lead = leadsByUuidLocal[conversation.lead_uuid];
                if (!lead) { return false; }
                const leadFirstName = lead.name?.split(' ')[0];
                const property = lead.property_id ? properties.find((o) => o.id === lead.property_id) : null;
                const propertyAddress = property?.street_address;
                const propertyTourLink = property?.showing_scheduling_link?.link;
                const propertyApplicationLink = property?.application_link?.link;
                const rent = property?.rent ? property.rent.toString() : undefined;
                const numberOfBedrooms = property?.num_bedrooms ? property.num_bedrooms.toString() : undefined;
                const numberOfBathrooms = property?.num_bathrooms ? property.num_bathrooms.toString() : undefined;
                const emailSignature = userSettings?.signatures?.email?.text;
                const smsSignature = userSettings?.signatures?.sms?.text;

                const variableMap: Record<PlaybookVariable, string> = {
                    [PlaybookVariable.LeadFirstName]: leadFirstName ?? '',
                    [PlaybookVariable.PropertyAddress]: propertyAddress ?? '',
                    [PlaybookVariable.PropertyTourLink]: propertyTourLink ?? '',
                    [PlaybookVariable.PropertyApplicationLink]: propertyApplicationLink ?? '',
                    [PlaybookVariable.Rent]: rent ?? '',
                    [PlaybookVariable.NumberOfBedrooms]: numberOfBedrooms ?? '',
                    [PlaybookVariable.NumberOfBathrooms]: numberOfBathrooms ?? '',
                    [PlaybookVariable.SMSSignature]: smsSignature ?? '',
                    [PlaybookVariable.EmailSignature]: emailSignature ?? '',
                    [PlaybookVariable.YourName]: loggedInUser?.name ?? '',
                    [PlaybookVariable.YourFirstName]: loggedInUser?.name?.split(' ')[0] ?? '',
                };

                return !!getMissingVariable(
                    formatPlaybookMessageForBackend(option.value as string),
                    variableMap,
                );
            });
        };
    }, [selectedConversations, leadsByUuidLocal, properties, userSettings]);

    const handleSendMessage = async (event: React.FormEvent) => {
        event.preventDefault();
        setSending(true);

        try {
            const validConversations = selectedConversations.filter((conversation) => {
                const lead = leadsByUuidLocal[conversation.lead_uuid];
                if (lead?.email?.endsWith('@example.com')) {
                    console.debug('Not sending an email to example.com');
                    return false;
                }

                return true;
            });

            await sendMessages(validConversations.filter(conversation => {
                const lead = leadsByUuidLocal[conversation.lead_uuid];
                if (!lead) {
                    console.error('Lead not found for conversation', conversation);
                    return false;
                }
                const sendChannel = getBestChannelForConversation(conversation, lead);
                if (!sendChannel) {
                    console.warn('No valid message channel for conversation', conversation);
                    return false;
                }

                return true;

            }).map((conversation) => {
                // checked in filter above
                const lead = leadsByUuidLocal[conversation.lead_uuid]!;

                // checked in filter above
                const sendChannel = getBestChannelForConversation(conversation, lead)!;

                const leadFirstName = lead?.name?.split(' ')[0];

                const property = properties.find((o) => o.id === lead.property_id);
                const propertyAddress = property?.street_address;
                const propertyTourLink = property?.showing_scheduling_link?.link;
                const propertyApplicationLink = property?.application_link?.link;
                const rent = property?.rent ? property?.rent.toString() : undefined;
                const numberOfBedrooms = property?.num_bedrooms ? property?.num_bedrooms.toString() : undefined;
                const numberOfBathrooms = property?.num_bathrooms ? property?.num_bathrooms.toString() : undefined;
                const emailSignature = userSettings?.signatures?.email?.text;
                const smsSignature = userSettings?.signatures?.sms?.text;

                const variableMap: Record<PlaybookVariable, string> = {
                    [PlaybookVariable.LeadFirstName]: leadFirstName ?? '',
                    [PlaybookVariable.PropertyAddress]: propertyAddress ?? '',
                    [PlaybookVariable.PropertyTourLink]: propertyTourLink ?? '',
                    [PlaybookVariable.PropertyApplicationLink]: propertyApplicationLink ?? '',
                    [PlaybookVariable.Rent]: rent ?? '',
                    [PlaybookVariable.NumberOfBedrooms]: numberOfBedrooms ?? '',
                    [PlaybookVariable.NumberOfBathrooms]: numberOfBathrooms ?? '',
                    [PlaybookVariable.SMSSignature]: smsSignature ?? '',
                    [PlaybookVariable.EmailSignature]: emailSignature ?? '',
                    [PlaybookVariable.YourName]: loggedInUser?.name ?? '',
                    [PlaybookVariable.YourFirstName]: loggedInUser?.name?.split(' ')[0] ?? '',
                };

                const messageWithVariablesFilled = snippetContentsWithVariablesFilled(
                    formatPlaybookMessageForBackend(message),
                    variableMap,
                );

                return {
                    leadUuid: conversation.lead_uuid,
                    channel: sendChannel,
                    message: messageWithVariablesFilled,
                };
            }));

            setSelectedConversations([]);
            onClose();
        } finally {
            setMessage('');
            setSending(false);
        }
    };

    const setReadStatus = async (status: ReadStatus) => {
        const leadUuids = unreadConversations.map((o) => o.lead_uuid);

        await updateReadStatus({
            leadUuids,
            status,
        });
    };

    const setOwnership = async () => {
        const leadsToUpdate: ILead[] = [];
        unownedConversations.forEach((conversation) => {
            const lead = leadsByUuidLocal[conversation.lead_uuid];
            if (!lead) { return; }
            leadsToUpdate.push(lead);
        });

        if (!leadsToUpdate.length) {
            return;
        }

        await updateLeads(leadsToUpdate.map(lead => ({
            ...lead,
            owner: loggedInUser.id
        })));
    };

    const deselectUnreadMessages = () => {
        setSelectedConversations(
            selectedConversations.filter((conversation) => {
                return !unreadConversations.includes(conversation);
            })
        );
    };

    const deselectUnownedLeads = () => {
        setSelectedConversations(
            selectedConversations.filter((conversation) => {
                return !unownedConversations.includes(conversation);
            })
        );
    };

    const deselectLeadsWithMissingVariables = () => {
        setSelectedConversations(
            selectedConversations.filter((conversation) => {
                return !leadsWithMissingVariables.includes(conversation);
            })
        );
    };

    const hasUnreadConversations = unreadConversations.length > 0;
    const hasUnownedConversations = unownedConversations.length > 0;
    const hasLeadsWithMissingVariables = leadsWithMissingVariables.length > 0;
    const allLeadsHaveMissingVariables = leadsWithMissingVariables.length === selectedConversations.length;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Send a bulk message</DialogTitle>

            <DialogContent>
                {hasUnreadConversations &&
                    <FlexColumn mb={2}>
                        <Alert
                            severity="warning"
                        >
                            You have selected {unreadConversations.length} unread {pluralize('conversation', unreadConversations.length)}. How do you want to proceed?
                        </Alert>
                        <Flex justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                color="dark"
                                onClick={deselectUnreadMessages}
                            >
                                Deselect unread
                            </Button>
                            <LoadingButton
                                variant="outlined"
                                loading={updateReadStatusVariables?.status === ReadStatus.READ && isUpdatingReadStatus}
                                onClick={() => setReadStatus(ReadStatus.READ)}
                            >
                                Mark read
                            </LoadingButton>
                        </Flex>
                    </FlexColumn>
                }

                {hasLeadsWithMissingVariables && !allLeadsHaveMissingVariables &&
                    <FlexColumn mb={2}>
                        <Alert
                            severity="warning"
                        >
                            You have selected {leadsWithMissingVariables.length} {pluralize('lead', leadsWithMissingVariables.length)} with missing variables.
                        </Alert>
                        <Flex justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                color="dark"
                                onClick={deselectLeadsWithMissingVariables}
                            >
                                Deselect conversations
                            </Button>
                        </Flex>
                    </FlexColumn>
                }

                {hasUnownedConversations && !hasUnreadConversations &&
                    <FlexColumn mb={2}>
                        <Alert
                            severity="warning"
                        >
                            You have selected {unownedConversations.length} {pluralize('conversation', unownedConversations.length)} that you do not own. How do you want to proceed?
                        </Alert>
                        <Flex justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                color="dark"
                                onClick={deselectUnownedLeads}
                            >
                                Deselect unowned
                            </Button>
                            <LoadingButton
                                variant="outlined"
                                // color="dark"
                                loading={isUpdatingLeads}
                                onClick={setOwnership}
                            >
                                Take Ownership
                            </LoadingButton>
                        </Flex>
                    </FlexColumn>
                }

                <form ref={formRef} onSubmit={handleSendMessage}>
                    <SnippetTemplateMessage
                        value={formatPlaybookMessageForFrontend(message)}
                        onChange={(newValue) => setMessage(newValue)}
                    />
                </form>

                <DialogContentText mt={2}>
                    {getActionSummaryText(numEmails, numSms, numFacebookMessages)}
                </DialogContentText>
            </DialogContent>

            <DialogActions>
                <SelectSnippet
                    onChange={(value) => {
                        setMessage(value || '');
                    }}
                    size="small"
                    fullWidth
                    placeholder="Select a snippet"
                    withFadedPlaceholder
                    getOptionDisabled={getOptionDisabled}
                />
                <Button
                    variant="outlined"
                    color="dark"
                    onClick={onClose}
                    disabled={isSending}
                >
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    onClick={() => formRef.current?.requestSubmit()}
                    loading={isSending}
                    disabled={hasUnreadConversations || hasUnownedConversations || hasLeadsWithMissingVariables}
                >
                    Send
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

const getActionSummaryText = (
    numEmails: number,
    numSms: number,
    numFacebookMessages: number,
) => {
    if (!numEmails && !numSms && !numFacebookMessages) {
        return null;
    }

    return `This action will send ${numEmails} ${pluralize('email', numEmails)}, ${numSms} SMS ${pluralize('message', numSms)}, and ${numFacebookMessages} Facebook Messenger ${pluralize('message', numFacebookMessages)}.`;
};
